import {inject, injectable} from 'inversify';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';

/**
 * Row shape returned by `GET /v1/api/portal/devices`.
 * Field names mirror the backend DTO (ManageDevicesController).
 * `id` is the MongoDB ObjectId hex string used as the sessionId
 * when revoking.
 */
export interface DeviceRow {
    id: string;
    deviceLabel: string | null;
    ip: string | null;
    lastUsedAt: string | null;
    createdAt: string | null;
}

/**
 * Portal-side service for managing the signed-in user's active
 * sessions (refresh-token records) per auth-refresh Task 23.
 *
 * Wraps the portal routes exposed on professorx:
 *   - `GET  /v1/api/portal/devices`        — list sessions
 *   - `POST /v1/api/portal/devices/revoke` — revoke one session
 *
 * Uses constructor `@inject` (NOT `@lazyInject`) — this is the
 * service convention; React components use `@lazyInject`.
 */
@injectable()
export class DevicesService {
    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
    ) {}

    /**
     * List the current user's active sessions.
     * Returns an array of `DeviceRow` objects, matching the backend
     * DTO field names exactly (`id`, `deviceLabel`, `ip`, `lastUsedAt`).
     */
    list(): Observable<DeviceRow[]> {
        return this.http
            .get<DeviceRow[]>(this.apiConfig.getDevicesEndpoint())
            .pipe(
                ResponsePipe,
                map((rows) => rows ?? []),
            );
    }

    /**
     * Revoke a single session by its id (the ObjectId hex string
     * returned from `list()`). The backend expects `{ sessionId }`
     * as the request body and performs an ownership check server-side.
     */
    revoke(sessionId: string): Observable<{error: boolean}> {
        return this.http
            .post<{error: boolean}>(
                this.apiConfig.getRevokeDeviceEndpoint(),
                {sessionId},
            )
            .pipe(ResponsePipe);
    }
}
